<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { storeToRefs } from "pinia"
import { RouterView, useRoute } from "vue-router"

import { useI18nStore } from "@/stores/i18n"
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"

import Background from "@/components/af/Background/Background.vue"
import ShortCut from "@/components/dev/ShortCut.vue"
import PreviousStateModal from "@/components/af/PreviousStateModal/PreviousStateModal.vue"
import LeaveAppModal from "@/components/af/LeaveAppModal/LeaveAppModal.vue"
import ExitIframeModal from "@/components/af/ExitIframeModal/ExitIframeModal.vue"
// import HelpAudioModal from "@/components/af/Help/HelpAudioModal.vue"
import LeaveWindowModal from "@/components/af/LeaveWindowModal/LeaveWindowModal.vue"

import { useCookies } from "@vueuse/integrations/useCookies"
import { hexToHSL, hexToRgb } from "@/lib/utils"

import HeaderControls from "@/components/af/HeaderControls/HeaderControls.vue"
import { COOKIES } from "./config/constants"

const userStore = useUserStore()
const { getUserUuid } = storeToRefs(userStore)
const configStore = useConfigStore()

const { currentLocale } = storeToRefs(useI18nStore())
const { appReady } = storeToRefs(useConfigStore())

const afAuthSession = useCookies().get(COOKIES.AUTH_SESSION)
const branding = afAuthSession?.branding ?? useCookies().get(COOKIES.BRANDING)

const style = computed(() => ({
  "--af-branding-cta": `hsl(${hexToHSL(branding?.color?.cta || "#ff2f92")})`,
  "--af-branding-cta-hover": `hsl(${hexToHSL(
    branding?.color?.cta || "#e6006d",
    {
      lightness: -10,
    }
  )})`,
  "--af-branding-theme": branding?.color?.theme || "#ff2f92",
  "--af-branding-border": branding?.color?.theme ? "#f1f3f4" : "#ffd5e9",
  "--af-gradient-start-rgb": hexToRgb(
    branding?.background_colors?.primary || "#ff2f92"
  ),
  "--af-gradient-end-rgb": hexToRgb(
    branding?.background_colors?.secondary || "#f59f00"
  ),
  "--af-gradient-start": `hsl(${hexToHSL(branding?.background_colors?.primary || "#ff2f92")})`,
  "--af-gradient-end": `hsl(${hexToHSL(branding?.background_colors?.secondary || "#f59f00")})`,
  "--af-gradient-start-rgb-10": `rgba(${hexToRgb(branding?.background_colors?.primary || "#ff2f92", 0.1)})`,
  "--af-gradient-end-rgb-10": `rgba(${hexToRgb(branding?.background_colors?.secondary || "#f59f00", 0.1)})`,
  "--af-gradient-start-rgb-20": `rgba(${hexToRgb(branding?.background_colors?.primary || "#ff2f92", 0.2)})`,
  "--af-gradient-end-rgb-20": `rgba(${hexToRgb(branding?.background_colors?.secondary || "#f59f00", 0.2)})`,
  "--af-gradient-start-rgb-30": `rgba(${hexToRgb(branding?.background_colors?.primary || "#ff2f92", 0.3)})`,
  "--af-gradient-end-rgb-30": `rgba(${hexToRgb(branding?.background_colors?.secondary || "#f59f00", 0.3)})`,
  "--af-gradient-start-rgb-50": `rgba(${hexToRgb(branding?.background_colors?.primary || "#ff2f92", 0.5)})`,
  "--af-gradient-end-rgb-50": `rgba(${hexToRgb(branding?.background_colors?.secondary || "#f59f00", 0.5)})`,
  "--af-gradient-start-rgb-65": `rgba(${hexToRgb(branding?.background_colors?.primary || "#ff2f92", 0.65)})`,
  "--af-gradient-end-rgb-65": `rgba(${hexToRgb(branding?.background_colors?.secondary || "#f59f00", 0.65)})`,
  "--af-branding-text": branding?.text_color || "#484d51",
  "--af-branding-heading": branding?.header_color || "#61686e",
  "--af-branding-accent": branding?.color?.cta || "#ff2f92",
  "--af-branding-border-avatar": branding?.color?.theme || "#fce2b2",
  "--af-branding-progressbar": branding?.color?.theme || "#4ebac7",
  "--af-branding-text-accent": branding?.color?.theme || "#029caf",
  "--bg-checkbox": branding?.color.cta || "#ff2f92",
}))

const isMounted = ref(false)
onMounted(() => {
  const root = document.body.style
  Object.entries(style.value).forEach(([key, value]) => {
    root.setProperty(key, value)
  })

  setTimeout(() => {
    isMounted.value = true
  }, 2000)
})

// const devMode = process.env.NODE_ENV === "development"

// Check for iframe
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Si l'accès à window.top est bloqué (cross-origin), on est dans une iframe
    return true
  }
}

const route = useRoute()
const path = computed(() => route.path)

const isDevModeSkipAssessment = computed(() => {
  const isDevEnv =
    configStore.environment !== "production" ||
    configStore.urls.app.includes("simu.assessfirst.com")
  const isAssessmentSkipable = [
    "shape",
    "swipe",
    "drive",
    "brain",
    "move",
  ].some((assessment) => path.value.includes(assessment))
  return isDevEnv && isAssessmentSkipable
})
</script>

<template>
  <!-- background -->
  <Background />

  <!-- dev utils -->
  <!-- <DevBox v-if="devMode" /> -->

  <!-- Swipe: Quick actions -->
  <ShortCut v-if="isDevModeSkipAssessment" />

  <!-- app content is reactive to current locale -->
  <main
    :class="[
      'text-afbranding-text relative flex min-h-dvh w-full flex-col items-center justify-start lg:justify-center lg:py-8',
      currentLocale.iso_code_639 === 'ar-SA' && 'arabic',
    ]"
    :key="currentLocale.iso_code_639"
  >
    <!-- header bar with lang, help, etc -->
    <div
      class="container mt-0 flex justify-between py-5 lg:absolute lg:top-0 lg:p-8"
    >
      <!-- box to teleports stuffs -->
      <div id="box-heading"></div>

      <div
        :class="[
          'flex items-start gap-2',
          currentLocale.iso_code_639 === 'ar-SA' ? 'mr-auto' : 'ml-auto',
        ]"
      >
        <!-- Top right controls -->
        <HeaderControls v-if="appReady" />
      </div>
    </div>

    <!-- main view -->
    <RouterView v-slot="{ Component }">
      <Transition
        mode="out-in"
        enter-active-class="duration-300 ease-out"
        enter-from-class="transform opacity-0 -translate-x-3"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="duration-300 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="transform opacity-0 translate-x-3"
      >
        <Component :is="Component" />
      </Transition>
    </RouterView>

    <!-- modal to handle audio -->
    <!-- <HelpAudioModal /> -->
    <!-- modal to handle and confirm previous action -->
    <PreviousStateModal />
    <!-- modal to leaving the App-->
    <LeaveAppModal />
    <!-- modal to trigger leaving window -->
    <LeaveWindowModal v-if="path.includes('voice/test')" />
    <!-- modal to leave app in new tab because of iframe -->
    <ExitIframeModal
      v-if="isMounted && isSafari && isInIframe() && !getUserUuid"
    />
  </main>
</template>
